<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';
import emblaCarouselVue from 'embla-carousel-vue';
import AutoHeight from 'embla-carousel-auto-height';
import ResponsiveImage from './ResponsiveImage';
import isMobile from '../utils/isMobile';

// Data.
const activeSlideId = ref(0);
const quotes = ref([]);
const logos = ref([]);
const sliderHeight = ref('');
const onMobile = ref(isMobile());

// Props.
const props = defineProps({
  headline: {
    type: String,
    required: true,
  },
  testimonials: {
    type: Object,
    required: true,
  },
});

// Methods.
const getEmblaAxis = () => {
  return onMobile.value ? 'x' : 'y';
};

// Ensure the passed ID is valid (s. BRAG2230001-373).
const validateSlideId = (id) => {
  if (id > quotes.value.length - 1) {
    return 0;
  }
  if (id < 0) {
    return quotes.value.length - 1;
  }

  return id;
};

const updateCurrentSlide = () => {
  activeSlideId.value = quotesSlider.value.selectedScrollSnap();
  moveToSlideIndex(activeSlideId.value);
};

const moveToSlideIndex = (i) => {
  const slideId = validateSlideId(i);

  quotesSlider.value.scrollTo(slideId);

  // Scroll thumb into view as well.
  logosSlider.value.scrollTo(slideId);
  activeSlideId.value = slideId;
};

const arrowClick = (isPrevious = false) => {
  moveToSlideIndex(
    isPrevious ? activeSlideId.value - 1 : activeSlideId.value + 1,
  );
};

const getSliderHeight = () => {
  // Need to get height, bc we're on Y direction.
  let height = 0;

  quotesSlider.value.slideNodes().forEach((slide) => {
    const contentHeight = slide.scrollHeight;
    height = Math.max(contentHeight, height);
  });

  sliderHeight.value = `${height}px`;
};

const setScaleClasses = (idx) => {
  // Add smaller scale decrease:
  // - when first is active -> add it to the last one;
  if (
    activeSlideId.value === logos.value[0]?.id &&
    idx === logos.value[logos.value.length - 1].id
  ) {
    return true;
  }

  // - when last is active -> add it to the first one;
  if (
    activeSlideId.value === logos.value[logos.value.length - 1]?.id &&
    idx === logos.value[0].id
  ) {
    return true;
  }

  // - when it's direct sibling of the active slide.
  if (activeSlideId.value - 1 === idx || activeSlideId.value + 1 === idx) {
    return true;
  }
};

const handleResize = () => {
  onMobile.value = isMobile();

  // Recalculate the slider height.
  getSliderHeight();

  // Reset active slide ID.
  activeSlideId.value = 0;

  // Change axis.
  // (Should work without re-init, but it doesn't.)
  const updatedOptions = {
    axis: getEmblaAxis(),
    loop: true,
    startIndex: activeSlideId.value,
  };

  quotesSlider.value.reInit(updatedOptions, plugins);
  logosSlider.value.reInit(updatedOptions);
};

// Mounted.
onMounted(() => {
  quotes.value = props.testimonials?.quotes;
  logos.value = props.testimonials?.client_logos;

  // Need to wait for slides to load.
  nextTick(() => {
    quotesSlider.value
      .on('init', getSliderHeight)
      .on('select', updateCurrentSlide)
      .on('resize', handleResize);
  });
});

// Embla init.
const sharedOptions = {
  axis: getEmblaAxis(),
  loop: true,
  startIndex: activeSlideId.value,
};
const plugins = onMobile.value ? [AutoHeight()] : [];

const [quotesContainer, quotesSlider] = emblaCarouselVue(
  {
    ...sharedOptions,
  },
  plugins,
);
const [logosContainer, logosSlider] = emblaCarouselVue({
  ...sharedOptions,
  containScroll: 'keepSnaps',
  dragFree: true,
});

// Watchers.
watch(activeSlideId, () => {
  activeSlideId.value = validateSlideId(activeSlideId.value);
});
</script>

<template>
  <div class="testimonial-slider" :style="{ '--slider-height': sliderHeight }">
    <!-- Quotes. -->
    <div class="testimonial-slider__quotes-wrapper">
      <span class="testimonial-slider__headline">
        {{ headline }}
      </span>

      <div class="testimonial-slider__viewport" ref="quotesContainer">
        <div class="testimonial-slider__quotes embla__container">
          <div
            v-for="quote in quotes"
            :key="quote.id"
            :class="{
              'testimonial-slider__quote--active': activeSlideId === quote.id,
            }"
            class="testimonial-slider__quote">
            <blockquote>
              {{ quote.statement }}
              <footer>
                <cite> {{ quote.author }} | {{ quote.author_position }} </cite>
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
    </div>

    <!-- Client logos. -->
    <div class="testimonial-slider__logos-wrapper">
      <div class="testimonial-slider__viewport" ref="logosContainer">
        <div class="testimonial-slider__logos">
          <div
            v-for="logo in logos"
            :key="logo.id"
            :class="{
              'testimonial-slider__logo--sibling': setScaleClasses(logo.id),
              'testimonial-slider__logo--active': activeSlideId === logo.id,
            }"
            class="testimonial-slider__logo"
            @click="moveToSlideIndex(logo.id)">
            <responsive-image
              :images="logo.client_logo ?? []"></responsive-image>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation arrows. -->
    <div class="testimonial__arrows">
      <button
        class="testimonial__arrow testimonial__arrow--prev"
        @click="arrowClick(true)"></button>
      <button
        class="testimonial__arrow testimonial__arrow--next"
        @click="arrowClick()"></button>
    </div>
  </div>
</template>
